import { Button } from "@heroui/react";
import type { ReactNode } from "react";
import { useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  colors: string[];
  onSelect: (color: string) => void;
  trigger: ReactNode;
  ariaLabel: string;
  showTransparentLabel?: boolean;
};

export function EditorToolbarColorPicker({
  isOpen,
  onOpenChange,
  colors,
  onSelect,
  trigger,
  ariaLabel,
  showTransparentLabel = false,
}: Props) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Calculate dropdown position relative to the trigger button.
  // Runs inside a portal so it's not clipped by parent overflow-auto containers.
  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [isOpen]);

  return (
    <div ref={triggerRef} className="relative">
      <Button
        size="sm"
        variant="light"
        isIconOnly
        onPress={() => onOpenChange(!isOpen)}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {trigger}
      </Button>
      {isOpen &&
        createPortal(
          <>
            {/* Click-outside overlay */}
            <button
              className="fixed inset-0 z-[100]"
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => onOpenChange(false)}
            />
            <div
              role="dialog"
              aria-label={ariaLabel}
              className="fixed z-[101] bg-content1 border border-default-200 rounded-lg shadow-md p-2 flex flex-wrap gap-1"
              style={{ top: pos.top, left: pos.left, minWidth: "140px" }}
            >
              {colors.map((color) => (
                <button
                  key={color}
                  aria-label={
                    color === "transparent" && showTransparentLabel
                      ? "No highlight"
                      : color
                  }
                  className="w-6 h-6 rounded border border-default-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  style={{
                    background: color === "transparent" ? "white" : color,
                    backgroundImage:
                      color === "transparent"
                        ? "repeating-linear-gradient(45deg, #ccc 0, #ccc 1px, white 0, white 50%)"
                        : undefined,
                    backgroundSize: "6px 6px",
                  }}
                  onClick={() => onSelect(color)}
                />
              ))}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
